/**
 * Open orders as a dense table (22 #3): LPO + supplier + project (or "No project linked"), value, delivered
 * progress, expected date with "202 d late", and a delivery state worked out from the lines. A row opens in
 * place (22 #4): its lines (ordered / delivered / remaining / unit price), the delivery notes on it, a check
 * that the lines add up to the LPO value, and Receive delivery / Edit LPO / Close · Cancel (confirmed).
 * On phones each LPO is a card with "Receive · 60 pcs to come".
 * Wave 3: "Chase supplier" on late LPOs (23 #9 — message, log, new ETA) and the expected date says when the supplier
 * was last asked or that it is a revised date ("was 12 Mar"); a line's part number opens "Where is it?" (22 #10).
 */
import { Fragment, useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowUpDown, Ban, Check, ChevronDown, ChevronRight, CloudOff, FolderOpen, PackageCheck, RotateCcw,
  SearchX, ShoppingCart, Sparkles, SquarePen, TriangleAlert, Truck,
} from 'lucide-react';
import api from '@/lib/api';
import { getErrorMessage } from '@/lib/errors';
import { formatDate, formatMoney, formatNumber } from '@/lib/format';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { EmptyState } from '@/components/ui/empty-state';
import { RowActions } from '@/components/ui/row-actions';
import { StatusPill } from '@/components/ui/status-pill';
import { useConfirm } from '@/components/ui/confirm-dialog';
import { notify } from '@/components/ui/notify';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuLabel, DropdownMenuRadioGroup, DropdownMenuRadioItem, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  DateCell, DestinationTag, LinkButton, MissingText, QtyProgress, TableScroll, UpperLabel, numClass, tableClass,
  tdClass, thClass, useIsPhone,
} from './SfUi';
import { LPO_SORTS, lpoValueCheck } from './procurementModel';
import { chaseState } from './chaseModel';

/** Under the expected date: "ETA asked 2 d ago" / "was 12 Mar" (23 #9). */
export function ChaseNote({ lpo, className }) {
  const state = chaseState(lpo);
  if (state.state === 'none') return null;
  return (
    <span className={cn('block whitespace-nowrap text-[11.5px] text-ink-3', className)} data-testid={`lpo-chase-note-${lpo?.id}`}>
      {state.text}{state.sub ? <><span className="text-ink-4"> · </span>{state.sub}</> : null}
    </span>
  );
}

const n0 = (value) => formatNumber(value, { decimals: 0 });
const n2 = (value) => formatNumber(value, { decimals: 2 });
const lower = (value) => String(value || '').trim().toLowerCase();

/** Close / Cancel / Reopen an LPO — confirmed first; the shell's toast offers Undo (recorded server-side). */
export function useLpoStatusChange(onChanged) {
  const confirm = useConfirm();
  return useCallback(async (item, status) => {
    const number = item.number || 'this LPO';
    const left = item.remaining > 0 ? `${formatNumber(item.remaining)} pcs` : null;
    const options = {
      Closed: {
        title: `Close ${number}?`,
        description: left
          ? `Closing says nothing more is coming on this LPO: its ${left} still to come leave the open orders and Forecast.`
          : 'Everything on it has arrived — closing moves it out of the open orders.',
        confirmLabel: 'Close LPO',
        tone: 'default',
        done: `${number} closed`,
      },
      Cancelled: {
        title: `Cancel ${number}?`,
        description: 'A cancelled LPO drops out of the open orders and Forecast.',
        consequences: item.delivered > 0 ? [`${formatNumber(item.delivered)} pcs already delivered stay recorded`] : undefined,
        confirmLabel: 'Cancel LPO',
        cancelLabel: 'Keep LPO',
        tone: 'danger',
        done: `${number} cancelled`,
      },
      Active: {
        title: `Reopen ${number}?`,
        description: 'It comes back to the open orders and Forecast with what is still to be delivered.',
        confirmLabel: 'Reopen LPO',
        tone: 'default',
        done: `${number} reopened`,
      },
    }[status];
    if (!options) return;
    const { done, ...dialog } = options;
    const ok = await confirm({
      ...dialog,
      subtitle: [item.supplier || 'Supplier missing', formatMoney(item.value), `${formatNumber(item.delivered)} of ${formatNumber(item.ordered)} pcs delivered`].join(' · '),
      onConfirm: async () => { await api.put(`/lpos/${item.id}`, { status }); },
    });
    if (!ok) return;
    notify.success(done);
    await onChanged?.();
  }, [confirm, onChanged]);
}

function lpoMenuItems(item, { canEdit, canReceive, onReceive, onEdit, onStatus, onChase, navigate }) {
  const status = lower(item.lpo?.status);
  const closed = status === 'closed' || status === 'cancelled';
  return [
    canReceive && item.isOpen && { label: 'Receive delivery', icon: Truck, onSelect: () => onReceive(item), testId: `lpo-menu-receive-${item.id}` },
    onChase && item.isOpen && item.remaining > 0 && { label: 'Chase supplier', icon: Sparkles, onSelect: () => onChase(item), testId: `lpo-menu-chase-${item.id}` },
    canEdit && { label: 'Edit LPO', icon: SquarePen, onSelect: () => onEdit(item), testId: `lpo-menu-edit-${item.id}` },
    item.projectName && item.projectId && { label: 'Open project', icon: FolderOpen, onSelect: () => navigate(`/projects/${item.projectId}`) },
    canEdit && !closed && { label: 'Close LPO', icon: PackageCheck, onSelect: () => onStatus(item, 'Closed'), testId: `lpo-menu-close-${item.id}` },
    canEdit && closed && { label: 'Reopen LPO', icon: RotateCcw, onSelect: () => onStatus(item, 'Active'), testId: `lpo-menu-reopen-${item.id}` },
    canEdit && status !== 'cancelled' && { label: 'Cancel LPO…', icon: Ban, danger: true, onSelect: () => onStatus(item, 'Cancelled'), testId: `lpo-menu-cancel-${item.id}` },
  ];
}

function SupplierProject({ item, canEdit, onLinkProject, className }) {
  return (
    <span className={cn('flex min-w-0 items-center gap-1 text-[12px] text-ink-3', className)}>
      {item.supplier ? <span className="min-w-[3rem] shrink truncate">{item.supplier}</span> : <MissingText className="shrink-0">Supplier missing</MissingText>}
      <span className="text-ink-4" aria-hidden="true">·</span>
      {item.projectName ? (
        <span className="truncate">{item.projectName}</span>
      ) : canEdit && onLinkProject ? (
        <button
          type="button"
          onClick={(event) => { event.stopPropagation(); onLinkProject(item); }}
          className="min-w-0 truncate rounded text-[11.5px] font-semibold text-status-amber-ink underline decoration-dotted underline-offset-2 hover:decoration-solid focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          title="This LPO is not linked to a project (or its project was deleted) — link it"
          data-testid={`lpo-link-project-${item.id}`}
        >
          No project linked
        </button>
      ) : (
        <MissingText className="shrink-0">No project linked</MissingText>
      )}
    </span>
  );
}

/** The LPO's delivery notes (GET /lpos/{id}). */
function useLpoDeliveries(id, refreshKey) {
  const [state, setState] = useState({ items: [], loading: true, error: '' });
  const load = useCallback(async () => {
    setState((prev) => ({ ...prev, loading: true, error: '' }));
    try {
      const res = await api.get(`/lpos/${id}`);
      setState({ items: Array.isArray(res.data?.delivery_notes) ? res.data.delivery_notes : [], loading: false, error: '' });
    } catch (err) {
      setState({ items: [], loading: false, error: getErrorMessage(err, 'Could not load the deliveries') });
    }
  }, [id]);
  useEffect(() => { load(); }, [load, refreshKey]);
  return { ...state, reload: load };
}

function DeliveriesOnLpo({ item, refreshKey }) {
  const { items, loading, error, reload } = useLpoDeliveries(item.id, refreshKey);
  if (loading) return <div className="space-y-2" aria-busy="true"><span className="ui-skeleton-shimmer block h-[52px] rounded-xl" /></div>;
  if (error) {
    return (
      <p className="flex flex-wrap items-center gap-x-2 text-[12px] text-status-red-ink">
        <CloudOff className="h-3.5 w-3.5" aria-hidden="true" />{error}<LinkButton onClick={reload}>Try again</LinkButton>
      </p>
    );
  }
  if (!items.length) return <p className="text-[12.5px] text-ink-3">Nothing delivered on this LPO yet.</p>;
  return (
    <ul className="space-y-2" data-testid={`lpo-deliveries-${item.id}`}>
      {items.map((dn) => {
        const pcs = (dn.line_items || []).reduce((sum, line) => sum + (Number(line.delivered_qty) || 0), 0);
        return (
          <li key={dn.id} className="rounded-xl border border-line bg-surface px-3 py-2">
            <div className="flex min-w-0 items-center gap-2">
              <Truck className="h-3.5 w-3.5 shrink-0 text-ink-3" aria-hidden="true" />
              <span className="min-w-0 truncate font-mono text-[12.5px] font-semibold text-ink">{dn.dn_number || '—'}</span>
              <span className="ml-auto shrink-0 text-[12px] font-semibold tabular-nums text-ink-2">{formatNumber(pcs)} pcs</span>
            </div>
            <div className="mt-1 flex min-w-0 items-center gap-2 text-[12px] text-ink-3">
              <DestinationTag type={dn.delivery_type} location={dn.destination_location} className="shrink-0" />
              <span className="truncate">
                {dn.delivery_date ? formatDate(dn.delivery_date) : 'No date'}
                {dn.delivery_type !== 'to_site' && dn.destination_location ? ` · ${dn.destination_location}` : ''}
              </span>
            </div>
            {dn.received_by ? <p className="mt-0.5 truncate text-[12px] text-ink-3">Received by {dn.received_by}</p> : null}
          </li>
        );
      })}
    </ul>
  );
}

function ValueCheck({ item, canEdit, onEdit }) {
  const check = lpoValueCheck(item);
  if (!item.lineCount) return <p className="text-[12.5px] text-ink-3">No lines on this LPO yet.</p>;
  if (check.ok) {
    return check.priced ? (
      <p className="flex items-center gap-1.5 text-[12.5px] text-ink-3"><Check className="h-3.5 w-3.5 text-status-green-ink" aria-hidden="true" />Lines add up to the LPO value</p>
    ) : (
      <p className="text-[12.5px] text-ink-3">The lines have no unit prices — the LPO value cannot be checked.</p>
    );
  }
  return (
    <div className="space-y-1" data-testid={`lpo-value-check-${item.id}`}>
      <p className="flex items-start gap-1.5 text-[12.5px] font-semibold text-status-amber-ink">
        <TriangleAlert className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
        <span>Lines add up to {n0(check.linesValue)} · LPO value says {n0(check.value)}</span>
      </p>
      {canEdit ? <LinkButton onClick={() => onEdit(item)} className="ml-5">Fix in Edit LPO</LinkButton> : null}
    </div>
  );
}

/** The opened LPO: lines, deliveries on it, the value check and its actions. */
export function LpoDetail({ item, canEdit, canReceive, onReceive, onEdit, onStatus, onChase, onTrace, refreshKey, compact = false }) {
  const navigate = useNavigate();
  return (
    <div className={cn('grid gap-5 border-l-[3px] border-brand bg-surface-2 px-4 pb-4 pt-3.5 sm:px-[18px]', !compact && 'lg:grid-cols-[minmax(0,1fr)_220px]')} data-testid={`lpo-detail-${item.id}`}>
      <div className="min-w-0">
        <UpperLabel className="mb-1.5">Lines</UpperLabel>
        {item.lineCount ? (
          <TableScroll>
            <table className="w-full min-w-[420px] border-separate border-spacing-0 text-[12.5px] text-ink [&_td+td]:pl-2 [&_th+th]:pl-2">
              <thead>
                <tr className="whitespace-nowrap text-[10.5px] font-bold uppercase tracking-[0.02em] text-ink-3">
                  <th className="pb-1.5 pr-2 text-left">Part</th>
                  <th className="pb-1.5 text-right">Ordered</th>
                  <th className="pb-1.5 text-right">Delivered</th>
                  <th className="pb-1.5 text-right">Remaining</th>
                  <th className="pb-1.5 text-right">Unit price</th>
                </tr>
              </thead>
              <tbody>
                {item.lines.map((line, index) => {
                  const ordered = Number(line.ordered_qty) || 0;
                  const delivered = Number(line.delivered_qty) || 0;
                  const remaining = line.remaining_qty ?? Math.max(0, ordered - delivered);
                  return (
                    <tr key={line.id || `${line.part_number}-${index}`} className="[&>td]:border-t [&>td]:border-line-2 [&>td]:py-1.5">
                      <td className="pr-2">
                        {onTrace && line.part_number ? (
                          <button
                            type="button"
                            onClick={() => onTrace({ part: line.part_number, projectId: item.projectName ? item.projectId : '', projectName: item.projectName, description: line.description })}
                            className="block rounded font-mono text-[12.5px] font-semibold text-ink underline decoration-dotted decoration-ink-4 underline-offset-2 hover:text-brand-ink hover:decoration-solid focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                            title="Where is it? — from the request to the site"
                            data-testid={`lpo-line-trace-${item.id}-${line.part_number}`}
                          >
                            {line.part_number}
                          </button>
                        ) : <span className="block font-mono text-[12.5px] font-semibold text-ink">{line.part_number}</span>}
                        {line.description ? <span className="block max-w-[160px] truncate text-[11.5px] text-ink-3" title={line.description}>{line.description}</span> : null}
                      </td>
                      <td className={numClass}>{formatNumber(ordered)}</td>
                      <td className={numClass}>{formatNumber(delivered)}</td>
                      <td className={cn('font-bold', numClass, remaining > 0 ? 'text-status-amber-ink' : 'text-ink-3')}>{formatNumber(remaining)}</td>
                      <td className={numClass}>{Number(line.unit_price) ? n2(line.unit_price) : <span className="text-ink-4">—</span>}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </TableScroll>
        ) : <p className="text-[12.5px] text-ink-3">No lines on this LPO yet.</p>}
      </div>
      <div className="min-w-0 space-y-3">
        <div>
          <UpperLabel className="mb-1.5">Deliveries on this LPO</UpperLabel>
          <DeliveriesOnLpo item={item} refreshKey={refreshKey} />
        </div>
        <div>
          <UpperLabel className="mb-1">Check</UpperLabel>
          <ValueCheck item={item} canEdit={canEdit} onEdit={onEdit} />
        </div>
      </div>
      <div className={cn('flex flex-wrap items-center gap-2', !compact && 'lg:col-span-2')}>
        {canReceive && item.isOpen ? (
          <Button variant="outline" size="sm" onClick={() => onReceive(item)} data-testid={`lpo-receive-${item.id}`}>
            <Truck aria-hidden="true" /> Receive delivery
          </Button>
        ) : null}
        {onChase && item.isLate ? (
          <Button variant="outline" size="sm" onClick={() => onChase(item)} data-testid={`lpo-chase-${item.id}`}>
            <Sparkles aria-hidden="true" /> Chase supplier
          </Button>
        ) : null}
        {canEdit ? (
          <Button variant="ghost" size="sm" onClick={() => onEdit(item)} data-testid={`lpo-edit-${item.id}`}>
            <SquarePen aria-hidden="true" /> Edit LPO
          </Button>
        ) : null}
        <RowActions
          label={`More actions for ${item.number}`}
          items={lpoMenuItems(item, { canEdit, canReceive: false, onReceive, onEdit: () => {}, onStatus, navigate }).filter((entry) => entry && entry.label !== 'Edit LPO' && entry.label !== 'Chase supplier')}
          size="sm"
          data-testid={`lpo-more-${item.id}`}
        />
        {canReceive && item.isOpen && item.remaining > 0 ? (
          <span className="ml-auto text-[12px] text-ink-3">
            Opens with the {formatNumber(item.remaining)} remaining pcs prefilled{item.remainingValue > 0 ? ` · ${formatMoney(item.remainingValue, { decimals: 0 })} to come` : ''}
          </span>
        ) : null}
      </div>
    </div>
  );
}

/** "Sort: Most late" menu; `compact` (phones) shows just "Sort" — the card head says the order. */
export function SortMenu({ value, onChange, compact = false }) {
  const current = LPO_SORTS.find((option) => option.key === value) || LPO_SORTS[0];
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" data-testid="lpo-sort" aria-label={`Sort: ${current.label}`} title={`Sort: ${current.label}`}>
          <ArrowUpDown aria-hidden="true" /> {compact ? 'Sort' : `Sort: ${current.label}`}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        <DropdownMenuLabel className="text-[11px] font-bold uppercase tracking-[0.06em] text-ink-3">Sort orders by</DropdownMenuLabel>
        <DropdownMenuRadioGroup value={current.key} onValueChange={onChange}>
          {LPO_SORTS.map((option) => <DropdownMenuRadioItem key={option.key} value={option.key}>{option.label}</DropdownMenuRadioItem>)}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function EmptyOrders({ filtered, onClearFilters, emptyTitle }) {
  return filtered ? (
    <EmptyState icon={SearchX} title="No orders match these filters" text="Try another search or remove a filter."
      action={onClearFilters ? <Button variant="outline" size="sm" onClick={onClearFilters}>Clear filters</Button> : null} data-testid="lpo-rows-empty" />
  ) : (
    <EmptyState icon={ShoppingCart} title={emptyTitle} text="New LPOs appear here until everything on them is delivered." data-testid="lpo-rows-empty" />
  );
}

function PhoneCards({ items, expanded, onToggle, actions }) {
  const navigate = useNavigate();
  return (
    <ul className="space-y-3 pt-3" data-testid="lpo-rows">
      {items.map((item) => {
        const open = expanded.has(item.id);
        return (
          <li key={item.id} className="overflow-hidden rounded-[16px] border border-line bg-surface shadow-card" data-testid={`lpo-row-${item.id}`}>
            <button type="button" onClick={() => onToggle(item.id)} aria-expanded={open} className="block w-full px-4 pb-2 pt-3.5 text-left">
              <span className="flex items-center justify-between gap-2">
                <span className="font-mono text-[14px] font-semibold text-ink">{item.number || '—'}</span>
                <StatusPill status={item.deliveryState} domain="lpo" size="sm" />
              </span>
              <span className="mt-1 flex min-w-0 flex-wrap items-center gap-x-1 text-[12.5px] text-ink-3">
                <span>{item.supplier || 'Supplier missing'}</span>
                <span aria-hidden="true">·</span>
                <span className="tabular-nums">{formatMoney(item.value, { decimals: 0 })}</span>
                <span aria-hidden="true">·</span>
                {item.projectName ? <span className="truncate">{item.projectName}</span> : <MissingText>No project linked</MissingText>}
              </span>
              <span className="mt-2.5 block h-1.5 overflow-hidden rounded-full bg-[color:var(--line)]">
                <span className={cn('block h-full rounded-full', item.deliveredPct >= 100 ? 'bg-status-green' : 'bg-brand')} style={{ width: `${Math.min(100, item.deliveredPct)}%` }} />
              </span>
              <span className="mt-1.5 flex items-center justify-between gap-2 text-[12.5px]">
                <span className="tabular-nums text-ink-2">{formatNumber(item.delivered)} of {formatNumber(item.ordered)} pcs</span>
                {item.expected ? (
                  <span className={cn('tabular-nums', item.isLate ? 'font-semibold text-status-red-ink' : 'text-ink-3')}>
                    {formatDate(item.expected, { year: 'auto' })}{item.isLate ? ` · ${formatNumber(item.daysLate)} d late` : ''}
                  </span>
                ) : <MissingText>No expected date</MissingText>}
              </span>
            </button>
            <div className="flex items-center gap-2 px-4 pb-3.5 pt-1">
              {actions.canReceive && item.isOpen ? (
                <Button variant="outline" className="h-10 flex-1" onClick={() => actions.onReceive(item)} data-testid={`lpo-receive-${item.id}`}>
                  <Truck aria-hidden="true" /> Receive{item.remaining > 0 && item.delivered > 0 ? ` · ${formatNumber(item.remaining)} pcs to come` : ''}
                </Button>
              ) : <span className="flex-1" />}
              <RowActions label={`Actions for ${item.number}`} items={lpoMenuItems(item, { ...actions, navigate })} />
            </div>
            {open ? <LpoDetail item={item} {...actions} compact /> : null}
          </li>
        );
      })}
    </ul>
  );
}

/**
 * props: items (summarizeLpo, already sorted), expanded (Set of ids), onToggle(id), canEdit, canReceive,
 * onReceive(item), onEdit(item), onLinkProject(item), onStatus(item, status), refreshKey, filtered,
 * onClearFilters, emptyTitle.
 */
export function LpoTable({ items, expanded, onToggle, canEdit, canReceive, onReceive, onEdit, onLinkProject, onStatus, onChase, onTrace, refreshKey, filtered, onClearFilters, emptyTitle = 'No open orders' }) {
  const isPhone = useIsPhone();
  const navigate = useNavigate();
  const actions = { canEdit, canReceive, onReceive, onEdit, onStatus, onChase, onTrace, refreshKey };
  if (!items.length) return <EmptyOrders filtered={filtered} onClearFilters={onClearFilters} emptyTitle={emptyTitle} />;
  if (isPhone) return <PhoneCards items={items} expanded={expanded} onToggle={onToggle} actions={actions} />;

  return (
    <TableScroll>
      <table className={cn(tableClass, 'min-w-[720px] table-fixed')} data-testid="lpo-rows">
        <colgroup>
          <col style={{ width: 34 }} /><col /><col style={{ width: 92 }} /><col style={{ width: 120 }} />
          <col style={{ width: 100 }} /><col style={{ width: 146 }} /><col style={{ width: 40 }} />
        </colgroup>
        <thead>
          <tr>
            <th className={thClass}><span className="sr-only">Open</span></th>
            <th className={thClass}>LPO / Supplier</th>
            <th className={cn(thClass, 'text-right')}>Value (AED)</th>
            <th className={thClass}>Delivered</th>
            <th className={thClass}>Expected</th>
            <th className={thClass}>Status</th>
            <th className={thClass}><span className="sr-only">Actions</span></th>
          </tr>
        </thead>
        <tbody>
          {items.map((item) => {
            const open = expanded.has(item.id);
            const cell = cn(tdClass, 'h-[52px] px-2.5 py-1.5', open && 'border-b-0 bg-brand-soft');
            return (
              <Fragment key={item.id}>
                <tr
                  className={cn('group cursor-pointer', !open && 'hover:[&>td]:bg-surface-hover')}
                  onClick={() => onToggle(item.id)}
                  data-testid={`lpo-row-${item.id}`}
                >
                  <td className={cn(cell, open && 'shadow-[inset_3px_0_0_var(--brand)]')}>
                    <button
                      type="button"
                      onClick={(event) => { event.stopPropagation(); onToggle(item.id); }}
                      aria-expanded={open}
                      aria-label={`${open ? 'Close' : 'Open'} ${item.number}`}
                      className="grid h-6 w-6 place-items-center rounded text-ink-3 hover:bg-surface-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      data-testid={`lpo-toggle-${item.id}`}
                    >
                      {open ? <ChevronDown className="h-3.5 w-3.5" aria-hidden="true" /> : <ChevronRight className="h-3.5 w-3.5" aria-hidden="true" />}
                    </button>
                  </td>
                  <td className={cell}>
                    <span className="block truncate font-mono text-[13.5px] font-semibold text-ink" title={item.number}>{item.number || '—'}</span>
                    <SupplierProject item={item} canEdit={canEdit} onLinkProject={onLinkProject} className="mt-0.5" />
                  </td>
                  <td className={cn(cell, numClass, 'whitespace-nowrap')}>{n2(item.value)}</td>
                  <td className={cell}>
                    {item.lineCount ? (
                      <QtyProgress done={item.delivered} total={item.ordered} barClassName="w-[56px]" label={`${item.number}: ${formatNumber(item.delivered)} of ${formatNumber(item.ordered)} pcs delivered`} />
                    ) : <span className="text-[12px] text-ink-3">No lines</span>}
                  </td>
                  <td className={cell}>
                    <DateCell value={item.expected} done={!item.isOpen} emptyLabel="No date" />
                    {item.isOpen ? <ChaseNote lpo={item.lpo} /> : null}
                  </td>
                  <td className={cell}><StatusPill status={item.deliveryState} domain="lpo" size="sm" /></td>
                  <td className={cn(cell, 'text-right')} onClick={(event) => event.stopPropagation()}>
                    <RowActions label={`Actions for ${item.number}`} items={lpoMenuItems(item, { ...actions, navigate })} size="sm" data-testid={`lpo-menu-${item.id}`} />
                  </td>
                </tr>
                {open ? (
                  <tr data-testid={`lpo-expansion-${item.id}`}>
                    <td colSpan={7} className="border-b border-line-2 p-0">
                      <LpoDetail item={item} {...actions} />
                    </td>
                  </tr>
                ) : null}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </TableScroll>
  );
}


